import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, Activity, ChevronRight, ChevronDown,
  Target, AlertCircle, Clock, Calendar, Pill, Zap, Beaker, Route
} from 'lucide-react';
import { TwinFloSubscriberCard } from '@/components/TwinFloCards';
import { TwinFloPredictionCard } from '@/components/TwinFloT2Cards';
import DashboardPerceptionBanner from '@/components/DashboardPerceptionBanner';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL INTERFACE — STATE PANEL
// ══════════════════════════════════════════════════════════════════════════════

// Pattern Direction Configuration (Emotional System)
const DIRECTION_CONFIG = {
  improving: { 
    icon: TrendingUp, 
    label: 'Trending toward stability',
    textColor: '#2E6B3B',
    bgColor: '#E1EDE4'
  },
  stable: { 
    icon: Minus, 
    label: 'Maintaining balance',
    textColor: '#4A5D4E',
    bgColor: '#E8ECE6'
  },
  fluctuating: { 
    icon: Activity, 
    label: 'Variable patterns',
    textColor: '#8C6239',
    bgColor: '#F5ECE1'
  },
  declining: { 
    icon: TrendingDown, 
    label: 'Needs attention',
    textColor: '#8A4343',
    bgColor: '#F2E6E6'
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// WEEKLY DIGEST (Collapsible)
// ══════════════════════════════════════════════════════════════════════════════

function WeeklyReflection({ digest }) {
  const [expanded, setExpanded] = useState(false);

  if (!digest || !digest.has_data) {
    return (
      <button 
        className="w-full py-4 px-6 flex items-center justify-between border-t border-[#EBE8E3] text-[#8A8681] hover:bg-[#F4F2EE]/50 transition-colors duration-300"
        onClick={() => setExpanded(!expanded)}
        data-testid="weekly-reflection-toggle"
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4" strokeWidth={1.25} />
          <span className="text-sm font-['Work_Sans']">Weekly Reflection</span>
        </div>
        <span className="text-xs">{digest?.message || 'Building your baseline...'}</span>
      </button>
    );
  }

  const { weekly_sentence, pattern_direction, key_observations, responsive_zone, risk_zone, weekly_focus } = digest;
  const dirConfig = DIRECTION_CONFIG[pattern_direction?.direction] || DIRECTION_CONFIG.stable;
  const DirIcon = dirConfig.icon;

  return (
    <div className="border-t border-[#EBE8E3]" data-testid="weekly-reflection">
      <button 
        className="w-full py-4 px-6 flex items-center justify-between text-[#5C5955] hover:bg-[#F4F2EE]/50 transition-colors duration-300"
        onClick={() => setExpanded(!expanded)}
        data-testid="weekly-reflection-toggle"
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: dirConfig.bgColor }}
          >
            <DirIcon className="w-5 h-5" style={{ color: dirConfig.textColor }} strokeWidth={1.25} />
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Weekly Reflection</p>
            <p className="text-sm font-medium font-['Outfit']" style={{ color: dirConfig.textColor }}>
              {pattern_direction?.label || 'Building baseline'}
            </p>
          </div>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-[#8A8681] transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} 
          strokeWidth={1.25} 
        />
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-6 animate-in fade-in duration-500" data-testid="weekly-reflection-content">
          {/* Weekly Sentence */}
          <p className="text-base text-[#4A4846] leading-relaxed font-['Work_Sans']" data-testid="weekly-sentence">
            {weekly_sentence}
          </p>

          {/* Observations */}
          {key_observations?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Key Observations</p>
              {key_observations.map((obs, i) => (
                <p key={i} className="text-sm text-[#5C5955] font-['Work_Sans'] pl-4 border-l-2 border-[#EBE8E3]">
                  {obs}
                </p>
              ))}
            </div>
          )}

          {/* Zones */}
          <div className="grid grid-cols-2 gap-4">
            {responsive_zone && (
              <div className="p-4 rounded-2xl" style={{ backgroundColor: DIRECTION_CONFIG.improving.bgColor }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2 font-['Work_Sans']" style={{ color: DIRECTION_CONFIG.improving.textColor + '99' }}>
                  Responding
                </p>
                <p className="text-sm font-['Work_Sans']" style={{ color: DIRECTION_CONFIG.improving.textColor }}>
                  {responsive_zone.observation}
                </p>
              </div>
            )}
            {risk_zone && (
              <div className="p-4 rounded-2xl" style={{ backgroundColor: DIRECTION_CONFIG.declining.bgColor }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2 font-['Work_Sans']" style={{ color: DIRECTION_CONFIG.declining.textColor + '99' }}>
                  Needs Attention
                </p>
                <p className="text-sm font-['Work_Sans']" style={{ color: DIRECTION_CONFIG.declining.textColor }}>
                  {risk_zone.observation}
                </p>
              </div>
            )}
          </div>

          {/* Weekly Focus */}
          <div className="p-5 rounded-2xl" style={{ backgroundColor: '#F5ECE1' }}>
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5" style={{ color: '#8C6239' }} strokeWidth={1.25} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-1 font-['Work_Sans']" style={{ color: '#8C623999' }}>
                  Weekly Focus
                </p>
                <p className="text-base font-medium font-['Outfit']" style={{ color: '#8C6239' }} data-testid="weekly-focus">
                  {weekly_focus}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// THOUGHT STREAM — Micro Insight Experience (NOT cards, NOT carousel)
// ══════════════════════════════════════════════════════════════════════════════
// Feels like the system speaking gently.
// 1 primary insight (center), 1-2 secondary (fade below).

function ThoughtStream({ insights, navigate }) {
  if (!insights?.insights?.length) return null;

  const items = insights.insights;
  const primary = items[0];
  const secondary = items.slice(1, 3);

  return (
    <div className="py-4" data-testid="thought-stream">
      {/* Primary insight — calm, centered */}
      <div className="text-center mb-2">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8681] mb-3 font-['Work_Sans']">
          {primary.title}
        </p>
        <p
          className="text-sm text-[#4A4745] leading-[1.85] font-['Work_Sans'] font-light max-w-lg mx-auto"
          data-testid="primary-insight"
        >
          {primary.explanation}
        </p>
        {primary.understand_this_link && (
          <button
            onClick={() => navigate(`/knowledge?article=${primary.understand_this_link}`)}
            className="mt-3 text-[11px] text-[#8C6B55]/70 hover:text-[#8C6B55] font-['Work_Sans'] transition-colors"
            data-testid="understand-this-btn"
          >
            Understand this
          </button>
        )}
      </div>

      {/* Secondary insights — fade below */}
      {secondary.length > 0 && (
        <div className="mt-6 space-y-3">
          {secondary.map((insight, i) => (
            <p
              key={i}
              className="text-center text-xs text-[#8A8681] leading-relaxed font-['Work_Sans'] font-light max-w-md mx-auto"
              style={{ opacity: 0.6 - i * 0.15 }}
              data-testid={`secondary-insight-${i}`}
            >
              {insight.explanation}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FOOD GUIDANCE TEASER — "What to eat now" mini-section
// ══════════════════════════════════════════════════════════════════════════════

function FoodGuidanceTeaser({ guidance, navigate }) {
  if (!guidance?.guidance) return null;

  const meals = guidance.guidance;
  const period = new Date().getHours() < 12 ? 'breakfast' : new Date().getHours() < 17 ? 'lunch' : 'dinner';
  const current = meals[period];

  if (!current?.recipe) return null;

  return (
    <button
      onClick={() => navigate('/eat')}
      className="w-full text-center py-5 px-6 rounded-2xl group transition-all duration-300 hover:bg-[#8C6B55]/[0.04]"
      data-testid="food-guidance-teaser"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8681] mb-2 font-['Work_Sans']">
        {period === 'breakfast' ? 'Morning nourishment' : period === 'lunch' ? 'Midday nourishment' : 'Evening nourishment'}
      </p>
      <p className="text-base text-[#2C2A29] font-['Outfit'] group-hover:text-[#8C6B55] transition-colors" data-testid="meal-suggestion">
        {current.recipe.title}
      </p>
      <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-1">
        Tap to explore your daily nutrition
      </p>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL STORY — The Entry Experience
// ══════════════════════════════════════════════════════════════════════════════
// Not a card. Not a section. Not "Pattern Memory."
// This is the opening relationship with the subscriber.
// Feels like: "Someone understands me" — NOT "System analyzed me"

function BiologicalStory({ narrative }) {
  if (!narrative || !narrative.has_data) return null;

  const { continuity_opening, pattern_evolution, momentum_signal, anchor_point } = narrative.narrative || {};
  if (!continuity_opening) return null;

  return (
    <div className="py-2 px-2" data-testid="biological-story">
      {/* Opening — the human connection */}
      <p
        className="text-[15px] sm:text-base text-[#4A4745] leading-[1.8] font-['Work_Sans'] font-light text-center"
        data-testid="story-opening"
      >
        {continuity_opening}
      </p>

      {/* Evolution observations — subtle, spaced */}
      {pattern_evolution && pattern_evolution.length > 0 && (
        <div className="mt-5 space-y-3">
          {pattern_evolution.map((observation, i) => (
            <p
              key={i}
              className="text-sm text-[#6B6762] leading-relaxed font-['Work_Sans'] font-light text-center"
              data-testid={`story-evolution-${i}`}
            >
              {observation}
            </p>
          ))}
        </div>
      )}

      {/* Momentum — where things are heading */}
      {momentum_signal && (
        <p
          className="mt-4 text-sm text-[#5C5955] leading-relaxed font-['Work_Sans'] font-light text-center"
          data-testid="story-momentum"
        >
          {momentum_signal}
        </p>
      )}

      {/* Anchor — ONE thing to hold onto */}
      {anchor_point && (
        <p
          className="mt-4 text-sm text-[#8C6B55] leading-relaxed font-['Work_Sans'] text-center"
          data-testid="story-anchor"
        >
          {anchor_point}
        </p>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LEVODOPA QUICK-ACCESS CARD — Medication timing at a glance
// ══════════════════════════════════════════════════════════════════════════════

function LevodopaCard({ subscriberId, navigate }) {
  const [status, setStatus] = useState(null);
  const [taking, setTaking] = useState(false);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/levodopa/status/${subscriberId}`)
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, [subscriberId]);

  if (!status?.has_protocol || status.total === 0) return null;

  const handleQuickTake = async () => {
    if (!status.next_pending) return;
    setTaking(true);
    try {
      const now = new Date();
      const actualTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      await axios.post(`${API}/levodopa/take-now/${subscriberId}`, {
        actual_time: actualTime,
        item_id: status.next_pending.item_id,
      });
      toast.success(`${status.next_pending.name} logged`);
      // Refresh
      const r = await axios.get(`${API}/levodopa/status/${subscriberId}`);
      setStatus(r.data);
    } catch {
      toast.error('Failed to log dose');
    } finally {
      setTaking(false);
    }
  };

  const fmt = (t) => {
    if (!t) return '--';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  return (
    <div
      className="bg-white/50 rounded-3xl overflow-hidden border border-[#EBE8E3] p-5"
      data-testid="levodopa-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#5C6B8A]/10 flex items-center justify-center">
          <Pill className="w-5 h-5 text-[#5C6B8A]" strokeWidth={1.25} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">Medication</p>
          <p className="text-[10px] text-[#8A8681] font-['Work_Sans']">
            {status.completed}/{status.total} doses today
          </p>
        </div>
        {status.next_pending && (
          <button
            onClick={handleQuickTake}
            disabled={taking}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#5C6B8A] text-white hover:bg-[#4C5B7A] transition-colors text-sm font-['Work_Sans']"
            data-testid="dashboard-take-now"
          >
            <Zap className="w-3.5 h-3.5" strokeWidth={2} />
            {taking ? 'Logging...' : 'Take Now'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl bg-[#F4F2EE]">
          <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-0.5">Last Taken</p>
          <p className="text-sm font-['Work_Sans'] text-[#2C2A29]" data-testid="last-taken-time">
            {status.last_taken ? `${status.last_taken.name} at ${fmt(status.last_taken.actual_time)}` : 'No dose yet today'}
          </p>
          {status.last_taken?.variance_minutes != null && Math.abs(status.last_taken.variance_minutes) > 5 && (
            <p className={`text-[10px] font-['Work_Sans'] mt-0.5 ${status.last_taken.variance_minutes > 0 ? 'text-[#8C6239]' : 'text-[#2E6B3B]'}`}>
              {status.last_taken.variance_minutes > 0 ? '+' : ''}{status.last_taken.variance_minutes} min from planned
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl bg-[#F4F2EE]">
          <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-0.5">Next Window</p>
          <p className="text-sm font-['Work_Sans'] text-[#2C2A29]" data-testid="next-dose-time">
            {status.next_pending ? `${status.next_pending.name} at ${fmt(status.next_pending.planned_time)}` : 'All doses completed'}
          </p>
        </div>
      </div>

      {/* View details link */}
      <button
        onClick={() => navigate('/daily')}
        className="w-full mt-3 text-center text-xs text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] transition-colors py-1"
        data-testid="view-daily-flow-link"
      >
        View full daily flow
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD STATE PANEL
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// FORMULATION NUDGES — Subscriber-safe phase preparation + transition cards.
// Only renders when the corresponding endpoint returns active data.
// ══════════════════════════════════════════════════════════════════════════════

function FormulationNudges({ phasePreparation, transitionPlan, navigate }) {
  const showPrepCard = !!(phasePreparation && phasePreparation.phase_active);
  const showPlanCard = !!(transitionPlan && transitionPlan.plan_active);

  if (!showPrepCard && !showPlanCard) return null;

  return (
    <div
      data-testid="formulation-nudges"
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      {showPrepCard && (
        <button
          type="button"
          data-testid="formulation-phase-preparation-card"
          onClick={() => navigate('/subscriber/phase-preparation')}
          className="text-left rounded-2xl border border-[#D8D4CE] bg-[#F4F2EE] hover:bg-[#EDE9E2] transition-colors duration-300 p-5"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#E8ECE6] flex items-center justify-center flex-shrink-0">
              <Beaker className="w-4 h-4 text-[#4A5D4E]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-1 font-['Work_Sans']">
                Your current phase preparation
              </p>
              <h3 className="text-base font-['Outfit'] text-[#2C2A29] mb-1">
                {phasePreparation.current_label || 'Preparation in progress'}
              </h3>
              <p className="text-xs text-[#5C5955] font-['Work_Sans']">
                View where your next phase is in the preparation process.
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#8A8681] mt-1 flex-shrink-0" />
          </div>
        </button>
      )}

      {showPlanCard && (
        <button
          type="button"
          data-testid="formulation-transition-plan-card"
          onClick={() => navigate('/subscriber/transition-plan')}
          className="text-left rounded-2xl border border-[#D8D4CE] bg-[#F4F2EE] hover:bg-[#EDE9E2] transition-colors duration-300 p-5"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#E1EDE4] flex items-center justify-center flex-shrink-0">
              <Route className="w-4 h-4 text-[#2E6B3B]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-1 font-['Work_Sans']">
                Your transition guidance
              </p>
              <h3 className="text-base font-['Outfit'] text-[#2C2A29] mb-1">
                Your next phase plan is ready
              </h3>
              <p className="text-xs text-[#5C5955] font-['Work_Sans']">
                See what to finish first, continue, or start next.
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#8A8681] mt-1 flex-shrink-0" />
          </div>
        </button>
      )}
    </div>
  );
}



export default function DashboardStatePanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    dailyGuidance: null,
    weeklyDigest: null,
    patternMemory: null,
    microInsights: null,
    foodGuidance: null,
    phasePreparation: null,
    transitionPlan: null,
  });

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    const fetchData = async () => {
      if (!subscriberId) return;

      try {
        const [guidanceRes, weeklyRes, patternMemRes, insightsRes, foodRes, phasePrepRes, transitionRes] = await Promise.all([
          axios.get(`${API}/daily-guidance/${subscriberId}`).catch(() => ({ data: null })),
          axios.get(`${API}/weekly-digest/${subscriberId}`).catch(() => ({ data: null })),
          axios.get(`${API}/pattern-memory/${subscriberId}?weeks=3`).catch(() => ({ data: null })),
          axios.get(`${API}/knowledge/micro-insights/${subscriberId}`).catch(() => ({ data: null })),
          axios.get(`${API}/recipes/daily-guidance/${subscriberId}`).catch(() => ({ data: null })),
          axios.get(`${API}/subscriber/formulation/phase-preparation`).catch(() => ({ data: null })),
          axios.get(`${API}/subscriber/formulation/transition-plan`).catch(() => ({ data: null })),
        ]);

        setData({
          dailyGuidance: guidanceRes.data,
          weeklyDigest: weeklyRes.data,
          patternMemory: patternMemRes.data,
          microInsights: insightsRes.data,
          foodGuidance: foodRes.data,
          phasePreparation: phasePrepRes.data,
          transitionPlan: transitionRes.data,
        });
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subscriberId]);

  if (loading || authLoading) {
    return (
      <BiologicalLayout systemState="stable">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-[#8A8681] font-['Work_Sans']">Loading...</div>
        </div>
      </BiologicalLayout>
    );
  }

  const { dailyGuidance, weeklyDigest, patternMemory, microInsights, foodGuidance, phasePreparation, transitionPlan } = data;
  const guidance = dailyGuidance?.has_data ? dailyGuidance : null;
  
  // Determine system state from pattern direction
  const systemState = guidance?.pattern_direction?.direction || 'stable';
  const dirConfig = DIRECTION_CONFIG[systemState];
  const DirIcon = dirConfig.icon;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <BiologicalLayout systemState={systemState}>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 space-y-8" data-testid="dashboard-state-panel">
        
        {/* ─── GREETING (Minimal) ───────────────────────────────────────────── */}
        <div className="text-center pt-4">
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mb-1">{getGreeting()}</p>
          <h1 
            className="text-4xl sm:text-5xl font-['Outfit'] font-light tracking-tight leading-none"
            style={{ color: '#2C2A29' }}
            data-testid="dashboard-greeting"
          >
            {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-2 max-w-md mx-auto">
            Let’s observe what your system is showing today.
          </p>
        </div>

        {/* ─── PERCEPTION SHIFT BANNER (or Participant badge) ───────────────── */}
        <DashboardPerceptionBanner />

        {/* ─── FORMULATION NUDGES (phase preparation + transition guidance) ─── */}
        <FormulationNudges
          phasePreparation={phasePreparation}
          transitionPlan={transitionPlan}
          navigate={navigate}
        />

        {/* ═══ TWINFLO STATE ═══════════════════════════════════════════════ */}
        <TwinFloSubscriberCard />
        <TwinFloPredictionCard />

        {/* ═══ YOUR ONGOING BIOLOGICAL STORY ═══════════════════════════════════ */}
        <BiologicalStory narrative={patternMemory} />

        {/* ═══ THOUGHT STREAM — Micro Insights ═══════════════════════════════════ */}
        <ThoughtStream insights={microInsights} navigate={navigate} />

        {/* ─── PATTERN DIRECTION (HERO) ─────────────────────────────────────── */}
        {guidance && (
          <div className="text-center space-y-6 py-8">
            <div className="flex justify-center">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center transition-colors duration-700"
                style={{ backgroundColor: dirConfig.bgColor }}
              >
                <DirIcon 
                  className="w-10 h-10 transition-colors duration-700" 
                  style={{ color: dirConfig.textColor }} 
                  strokeWidth={1} 
                />
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A8681] mb-2 font-['Work_Sans']">
                7-Day Pattern
              </p>
              <h2 
                className="text-2xl sm:text-3xl font-['Outfit'] font-normal tracking-tight transition-colors duration-700"
                style={{ color: dirConfig.textColor }}
                data-testid="pattern-direction"
              >
                {guidance.pattern_direction?.label || dirConfig.label}
              </h2>
            </div>
          </div>
        )}

        {/* ─── TODAY'S FOCUS (Centered, Strong) ─────────────────────────────── */}
        {guidance?.todays_focus && (
          <div 
            className="text-center py-10 px-8 rounded-3xl transition-colors duration-700"
            style={{ backgroundColor: dirConfig.bgColor + '40' }}
            data-testid="todays-focus-section"
          >
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A8681] mb-4 font-['Work_Sans']">
              Today's Focus
            </p>
            <h3 
              className="text-xl sm:text-2xl font-['Outfit'] font-medium tracking-tight mb-3"
              style={{ color: '#2C2A29' }}
              data-testid="todays-focus"
            >
              {guidance.todays_focus.action}
            </h3>
            {guidance.todays_focus.rationale && (
              <p className="text-sm text-[#5C5955] font-['Work_Sans'] leading-relaxed max-w-md mx-auto">
                {guidance.todays_focus.rationale}
              </p>
            )}
          </div>
        )}

        {/* ─── ONE-LINE INSIGHT (Soft) ──────────────────────────────────────── */}
        {guidance?.one_line_insight && (
          <p 
            className="text-center text-base text-[#5C5955] font-['Work_Sans'] leading-relaxed px-4"
            data-testid="one-line-insight"
          >
            {guidance.one_line_insight}
          </p>
        )}

        {/* ─── RISK SIGNAL (If Any) ─────────────────────────────────────────── */}
        {guidance?.risk_signal && (
          <div 
            className="flex items-center gap-4 py-4 px-6 rounded-2xl"
            style={{ backgroundColor: DIRECTION_CONFIG.declining.bgColor }}
            data-testid="risk-signal"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: DIRECTION_CONFIG.declining.textColor }} strokeWidth={1.25} />
            <p className="text-sm font-['Work_Sans']" style={{ color: DIRECTION_CONFIG.declining.textColor }}>
              {guidance.risk_signal.signal}
            </p>
          </div>
        )}

        {/* ─── EXECUTION SNAPSHOT (Minimal) ─────────────────────────────────── */}
        {guidance?.execution_snapshot && (
          <div className="flex items-center justify-center gap-8 py-4 text-center">
            <div>
              <p className="text-3xl font-['Outfit'] font-light" style={{ color: '#2C2A29' }}>
                {guidance.execution_snapshot.completed}
                <span className="text-xl text-[#8A8681]">/{guidance.execution_snapshot.total}</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mt-1">
                Today
              </p>
            </div>
            <div className="h-10 w-px bg-[#EBE8E3]" />
            <div>
              <p className="text-3xl font-['Outfit'] font-light" style={{ color: dirConfig.textColor }}>
                {guidance.execution_snapshot.adherence_percentage}%
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mt-1">
                Adherence
              </p>
            </div>
          </div>
        )}

        {/* ═══ LEVODOPA QUICK-ACCESS CARD ══════════════════════════════════ */}
        <LevodopaCard subscriberId={subscriberId} navigate={navigate} />

        {/* ─── OPEN DAILY CTA ───────────────────────────────────────────────── */}
        <div className="text-center py-6">
          <Button
            variant="ghost"
            className="rounded-full px-8 py-3 text-[#5C5955] hover:text-[#2C2A29] hover:bg-[#EBE8E3] transition-all duration-300 font-['Work_Sans']"
            onClick={() => navigate('/daily')}
            data-testid="open-daily-btn"
          >
            <Clock className="w-4 h-4 mr-2" strokeWidth={1.25} />
            Open Today's Flow
            <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.25} />
          </Button>
        </div>

        {/* ─── FOOD GUIDANCE TEASER ─────────────────────────────────────────── */}
        <FoodGuidanceTeaser guidance={foodGuidance} navigate={navigate} />

        {/* ─── WEEKLY REFLECTION (Collapsible, Bottom) ──────────────────────── */}
        <div className="bg-white/50 rounded-3xl overflow-hidden border border-[#EBE8E3]">
          <WeeklyReflection digest={weeklyDigest} />
        </div>

      </div>
    </BiologicalLayout>
  );
}
